import React, { useEffect } from "react";
import { Alert, Center, Loader, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetMcappsProjectPnsQuery } from "../../../openapi";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import PnsTreePanel from "./PnsTreePanel";
import {
  selectExpandedPnsIds,
  selectSelectedParentPnsIds,
  selectSelectedPnsIds,
  setExpandedPnsIdsInRedux,
  setSelectedParentPnsIdsInRedux,
  setSelectedPnsIdsInRedux,
} from "./pnsSelectionSlice";
import {
  selectGetMcappsPnsArgs,
  selectMcappsPnsItems,
  selectNextExpandedPnsNodeIds,
  selectSelectedParentPnsIdsFromSelection,
  selectValidSelectedPnsIds,
} from "./pnsSelectors";

export const PnsTreePanelContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const pnsArgs = useAppSelector(selectGetMcappsPnsArgs);
  const pnsItems = useAppSelector(selectMcappsPnsItems);
  const selectedIds = useAppSelector(selectSelectedPnsIds);
  const validSelectedIds = useAppSelector(selectValidSelectedPnsIds);
  const selectedParentIds = useAppSelector(selectSelectedParentPnsIds);
  const selectedParentPnsIds = useAppSelector(selectSelectedParentPnsIdsFromSelection);
  const expandedIds = useAppSelector(selectExpandedPnsIds);
  const nextExpandedIds = useAppSelector(selectNextExpandedPnsNodeIds);

  const { data, isLoading, isFetching, isError, error } = useGetMcappsProjectPnsQuery(
    pnsArgs ?? skipToken
  );

  useEffect(() => {
    if (!data) return;
    if (validSelectedIds.length !== selectedIds.length) {
      dispatch(setSelectedPnsIdsInRedux(validSelectedIds));
    }

    if (
      nextExpandedIds.length !== expandedIds.length ||
      nextExpandedIds.some((id, idx) => id !== expandedIds[idx])
    ) {
      dispatch(setExpandedPnsIdsInRedux(nextExpandedIds));
    }
  }, [data, dispatch, expandedIds, nextExpandedIds, selectedIds, validSelectedIds]);

  useEffect(() => {
    if (!data) {
      if (selectedParentIds.length > 0) {
        dispatch(setSelectedParentPnsIdsInRedux([]));
      }
      return;
    }

    if (
      selectedParentPnsIds.length !== selectedParentIds.length ||
      selectedParentPnsIds.some((id, idx) => id !== selectedParentIds[idx])
    ) {
      dispatch(setSelectedParentPnsIdsInRedux(selectedParentPnsIds));
    }
  }, [data, dispatch, selectedParentIds, selectedParentPnsIds]);

  if (!pnsArgs) {
    return (
      <Center h="100%">
        <Text size="sm" c="dimmed">
          Velg prosjekt for å vise PNS
        </Text>
      </Center>
    );
  }

  if (isLoading) {
    return (
      <Center h="100%">
        <Loader size="sm" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert color="red" title="Kunne ikke hente PNS" h="100%">
        <Text size="sm">
          {typeof (error as any)?.error === "string"
            ? (error as any).error
            : "Ukjent feil ved henting av PNS-data."}
        </Text>
      </Alert>
    );
  }

  return (
    <PnsTreePanel
      title={isFetching ? "PNS (oppdaterer…)" : "PNS"}
      items={pnsItems}
      selectedIds={selectedIds}
      onSelectedIdsChange={(ids) => dispatch(setSelectedPnsIdsInRedux(ids))}
      expandedIds={expandedIds}
      onExpandedIdsChange={(ids) => dispatch(setExpandedPnsIdsInRedux(ids))}
    />
  );
};

export default PnsTreePanelContainer;
